import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { listPapers, clearHistory, logout } from '../services/api';
import '../styles/PaperListPage.css';

function PaperListPage() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isInstructor = user?.role === 'instructor';

  // The toggle is gone; clear any value it left behind.
  useEffect(() => {
    localStorage.removeItem('paperbuddy_mode');
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await listPapers();
        if (!cancelled) {
          setPapers(data.papers || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Could not load papers. Is the backend running?');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const [clearing, setClearing] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleClearHistory = async () => {
    if (!user) return;
    if (!window.confirm('Delete all chat history for your account? This cannot be undone.')) return;
    setClearing(true);
    try {
      await clearHistory(user.id);
    } catch (e) {
      alert('Failed to clear history. Is the backend running?');
    } finally {
      setClearing(false);
    }
  };

  const header = (
    <header className="paper-list-header">
      <h1>PaperBuddy</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {user && (
          <span style={{ fontSize: '0.875rem', color: '#555' }}>
            {user.name || user.username || user.email}
          </span>
        )}
        <Link to="/change-password" className="paper-list-logout">
          Change password
        </Link>
        <button type="button" className="paper-list-logout" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="paper-list-page">
        {header}
        <div className="paper-list-loading">Loading papers…</div>
      </div>
    );
  }

  return (
    <div className="paper-list-page">
      {header}

      <main className="paper-list-main">
        <div className="paper-list-title-row">
          <h2>Choose a paper</h2>
          {isInstructor && (
            <button
              type="button"
              className="paper-list-clear-btn"
              onClick={handleClearHistory}
              disabled={clearing}
            >
              {clearing ? 'Clearing…' : 'Clear history'}
            </button>
          )}
          {/* No "Add paper" link: the admin lives at an unguessable path in
              production, and anything built into this bundle is served to every
              student, so the URL would be public. Instructors go to the admin
              directly. */}
        </div>

        {error && <p className="paper-list-error">{error}</p>}
        {!error && papers.length === 0 && (
          <p className="paper-list-empty">No papers yet. Add papers in the Django admin.</p>
        )}
        <ul className="paper-list">
          {papers.map((p) => (
            <li key={p.id}>
              <Link
                to={`/reading/${p.id}`}
                className="paper-list-link"
                onClick={() => {
                  // Only a paper they have never chatted about starts on the picker.
                  if (!isInstructor && !p.has_threads) {
                    localStorage.removeItem(`pb_student_home_dismissed_${p.id}`);
                  }
                }}
              >
                <span className="paper-list-name">{p.name}</span>
                <span className="paper-list-meta">
                  {p.last_page > 1 ? `Last read p.${p.last_page}` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

export default PaperListPage;
